import React, { useMemo } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { Text, Card } from 'react-native-paper';
import Icon from 'react-native-vector-icons/FontAwesome';
import { useSelector } from 'react-redux';
import type { RootState } from '../redux/store';

import T from '../Components/T';
const AssetHome = ({ navigation }: any) => {
  const gdata = useSelector((state: RootState) => state.gdata);

  // Check menu permissions for sub-features
  const canAccessCategories = useMemo(() =>
    gdata?.menu?.some(m => m.mn_link?.includes('/master/am')) ?? false,
    [gdata?.menu]
  );

  const canAccessAssets = useMemo(() =>
    gdata?.menu?.some(m => m.mn_link?.includes('/am') && m.mn_link?.includes('asset')) ?? false,
    [gdata?.menu]
  );

  const canAccessPurchase = useMemo(() =>
    gdata?.menu?.some(m => m.mn_link === '/entry/am-purchaseentry') ?? false,
    [gdata?.menu]
  );

  const canAccessReports = useMemo(() =>
    gdata?.menu?.some(m => m.mn_link?.includes('/report') && m.mn_link?.includes('am')) ?? false,
    [gdata?.menu]
  );

  const features = [
    {
      title: 'Categories',
      icon: 'list',
      description: 'Manage asset categories',
      screen: 'AmCategoryList',
      accessible: canAccessCategories
    },
    {
      title: 'Products',
      icon: 'cubes',
      description: 'Asset product master and templates',
      screen: 'AmProductList',
      accessible: canAccessCategories
    },
    {
      title: 'Asset Register',
      icon: 'cube',
      description: 'Assets with photos, department and age',
      screen: 'AmAssetRegister',
      accessible: canAccessAssets
    },
    {
      title: 'Purchase Entry',
      icon: 'shopping-cart',
      description: 'Record new asset purchase',
      screen: 'AmAssetPurchaseEntry',
      accessible: canAccessPurchase
    },
    {
      title: 'Reports',
      icon: 'bar-chart',
      description: 'Asset reports and analytics',
      screen: 'AmReports',
      accessible: canAccessReports
    }
  ];

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}><T>Asset Management</T></Text>
      </View>

      <View style={styles.grid}>
        {features.map((feature, index) => (
          !feature.accessible ? null : (
            <TouchableOpacity
              key={index}
              style={styles.card}
              onPress={() => navigation.navigate(feature.screen)}
            >
              <Card style={styles.cardContent}>
                <View style={styles.iconContainer}>
                  <Icon name={feature.icon} size={40} color="#2E7D32" />
                </View>
                <Text style={styles.cardTitle}>{feature.title}</Text>
                <Text style={styles.cardDescription}>{feature.description}</Text>
              </Card>
            </TouchableOpacity>
          )
        ))}
      </View>

      {!canAccessCategories && !canAccessAssets && !canAccessPurchase && !canAccessReports && (
        <View style={styles.noAccess}>
          <Text style={styles.noAccessText}><T>No Asset Management features available</T></Text>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5'
  },
  header: {
    padding: 20,
    backgroundColor: '#2E7D32',
    marginBottom: 10
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white'
  },
  grid: {
    padding: 10,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between'
  },
  card: {
    width: '48%',
    marginBottom: 15
  },
  cardContent: {
    padding: 15,
    borderRadius: 8
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: 10
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 5,
    textAlign: 'center'
  },
  cardDescription: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center'
  },
  noAccess: {
    padding: 20,
    alignItems: 'center'
  },
  noAccessText: {
    fontSize: 16,
    color: '#999'
  }
});

export default AssetHome;
